import { useLocale, useTranslations } from '@fshr/context/i18n-context';
import Button from '../button';
import Alert from '../icon/alert';
import { cn } from '@fshr/shared-react';
import HorizontalLine from '../horizontal-line';
import BevSafeText from '../bev-safe-text';
import { getUpdatedTimeLabel } from './shared/displayUpdated';

const displayUpdated = (
	date: string | undefined,
	t: (text: string, params?: (string | number)[]) => string,
	locale: string
) => {
	const timeLabel = getUpdatedTimeLabel(date, t, locale);
	if (!timeLabel) {
		return null;
	}

	return (
		<div className="Calendar-done-updated w-full flex justify-center header:justify-start items-center">
			<span className="text-caption">
				<BevSafeText>{timeLabel}</BevSafeText>
			</span>
		</div>
	);
};

type CalendarFooterProps = {
	isCheckoutDateRequired?: boolean;
	onSubmit?: () => unknown;
	onBlur?: () => unknown;
	onKeyDown?: (e: any) => unknown;
	valueLabel?: string;
	isMobile?: boolean;
	lastUpdated?: string;
	buttonDisabled?: boolean;
	showLegend?: boolean;
};

export function CalendarFooter(props: CalendarFooterProps) {
	const {
		isCheckoutDateRequired,
		onSubmit,
		valueLabel,
		isMobile,
		lastUpdated,
		buttonDisabled = false,
		showLegend = true,
		onBlur,
		onKeyDown,
	} = props;
	const t = useTranslations();
	const locale = useLocale();

	return (
		<div className="relative w-full">
			<div
				className={cn([
					isMobile ? 'fixed bottom-0 left-0 z-60 w-full pb-300 bg-white' : 'flex flex-col w-full',
				])}
			>
				<HorizontalLine additionalClasses="border-border-subtle" />
				<div className="flex flex-col bg-white spacing-card-padding-xs">
					{showLegend && (
						<div className="flex items-center justify-start">
							{isCheckoutDateRequired ? (
								<div className="flex gap-100">
									<div>
										<Alert />
									</div>
									<div>
										<span className="text-caption text-[#EA0A0A]">
											Please select check-out date
										</span>
									</div>
								</div>
							) : (
								<div
									className={
										isMobile
											? 'w-full flex flex-col items-center justify-center spacing-card-padding-xs spacing-stack-2xs'
											: 'w-full'
									}
								>
									<div className="legend-list flex flex-row spacing-inline-m">
										<div className="legend-item flex flex-row spacing-inline-2xs items-center justify-start">
											<div className="w-[16px] h-[16px] border border-dashed border-button-primary rounded-full"></div>
											<span className="text-caption">{t('Limited')}</span>
										</div>
										<div className="legend-item flex flex-row spacing-inline-2xs items-center justify-start">
											<div className="w-[16px] h-[1px] border border-text-disabled rounded-full"></div>
											<span className="text-caption">{t('Unavailable')}</span>
										</div>
									</div>
									{isMobile && displayUpdated(lastUpdated, t, locale)}
								</div>
							)}
						</div>
					)}
					<div className="Calendar-Done Calendar-Done--active">
						<div className="Calendar-Done-inner flex flex-row spacing-inline-m items-center justify-end">
							{!isMobile && displayUpdated(lastUpdated, t, locale)}
							<div className="Calendar-done-date-range">
								<span className="text-label text-nowrap">
									<BevSafeText>{valueLabel}</BevSafeText>
								</span>
							</div>
							<Button
								intent="filled"
								className="flexdates-done-button w-full max-w-[125px]"
								disabled={buttonDisabled}
								onClick={onSubmit}
								onKeyDown={onKeyDown}
								onBlur={onBlur}
							>
								{t('Apply')}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
